import * as React from 'react';
import { InteractionReportBlock, PVAPage, LoadingIndicatorMini, PVAUserSelect, LoadingIndicator } from '../../../components';
import { BaseRouteProps } from '../../../interfaces';
import { FilterStore, UserStore } from '../../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
import { ReportDataService } from '../../../services';
import { hasPermission } from '../../../utilities/User';
import { UserPermissions } from '../../../models';
import './InteractionReport.css';

interface InteractionReportProps extends BaseRouteProps<{}> {
    FilterStore?: FilterStore;
    UserStore?: UserStore;
}

@inject('FilterStore', 'UserStore') @observer
export class InteractionReport extends React.Component<InteractionReportProps, { loading: boolean, pdfLoader: boolean }> {
    private reportDataService = new ReportDataService(this.props.notificationService);

    constructor(props: InteractionReportProps) {
        super(props);

        this.state = {
            loading: false,
            pdfLoader: false
        };
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            // Filter to only show users who have completed the current survey version
            // AND are not Foundations users
            return this.props.FilterStore.users.filter(u => {
                if (hasPermission(u.userPermissions, UserPermissions.Foundations)) {
                    return false;
                }

                if (!this.surveyVersion) {
                    return u.userStyles && u.userStyles.length > 0;
                }

                return u.userStyles && u.userStyles.some(userStyle => {
                    return userStyle.style &&
                        userStyle.style.surveyVersion &&
                        userStyle.style.surveyVersion.id === this.surveyVersion!.id;
                });
            });
        }
        return [];
    }

    @computed get userId() {
        if (this.props.FilterStore && this.props.FilterStore.interactionReportUserId) {
            return this.props.FilterStore.interactionReportUserId;
        }
        return 0;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    @computed get selectedUserData() {
        return this.props.FilterStore!.interactionReportUserData;
    }

    private updateSurvey() {
        if (!this.surveyVersion) {
            this.setState({
                loading: false
            });
            return;
        }

        const userId = this.userId;

        if (userId) {
            this.setState({ loading: true });
            this.reportDataService.getManagersReportData(this.surveyVersion.id, userId).then((reportData) => {
                this.props.FilterStore!.interactionReportUserData = reportData;
                this.setState({
                    loading: false
                });
            }).catch(() => {
                this.setState({
                    loading: false
                });
            });
        } else {
            this.props.FilterStore!.interactionReportUserData = null;
            this.setState({
                loading: false
            });
        }
    }

    private update() {
        if (this.userId && !this.selectedUserData && !this.state.loading) {
            this.updateSurvey();
        }
    }

    public userChange(e: any) {
        const userId: number = Number(e.target.value);
        this.props.FilterStore!.interactionReportUserId = userId;
        this.props.FilterStore!.interactionReportUserData = null;
        this.updateSurvey();
    }

    private exportPDF() {
        if (this.selectedUserData && this.surveyVersion && this.userId) {
            this.setState({
                pdfLoader: true
            });
            this.reportDataService.getManagerReportDataPDF(this.surveyVersion.id, this.userId, 'InteractionReport', this.surveyReportType ? this.surveyReportType.id : undefined)
                .then(
                    (reportData: any) => {
                        this.setState(
                            {
                                pdfLoader: false
                            });
                    }, (err: any) => {
                        this.setState(
                            {
                                pdfLoader: false
                            });
                    });
        }
    }

    componentDidMount() {
        this.props.FilterStore!.interactionReportUserId = undefined;
        this.props.FilterStore!.interactionReportUserData = null;
        this.update();
    }

    componentWillReact() {
        this.update();
    }

    public render() {
        const defaultOptions = [{
            id: 0,
            value: '- Select User -'
        }];
        
        const actions = this.userId !== 0 ? [
            {
                header: 'Export Options',
                items: [
                    {
                        id: 'pdf-export',
                        name: 'PDF Export',
                        callback: () => this.exportPDF(),
                        icon: 'Icon awesome-file-pdf.svg'
                    }
                ]
            }
        ] : [];

        return <PVAPage>
            <PVAPage.Header actions={actions} actionsDisabled={this.state.loading || !this.userId}>
                <PVAPage.Header.Title>
                    <PVAPage.Header.Title.Text>
                        <span className="greyDark"> PVA Interaction Report <span className="trademark">TM</span> for: </span>
                        <div><PVAUserSelect defaultOptions={defaultOptions} options={this.users} selected={this.userId} onChange={(e: any) => this.userChange(e)} /></div>
                    </PVAPage.Header.Title.Text>
                </PVAPage.Header.Title>
            </PVAPage.Header>
            <PVAPage.Body>
                {this.state.loading && <LoadingIndicator />}
                {this.state.pdfLoader && <LoadingIndicator showPdfDescription={true} showBackground={true} showInPanel={true} />}
                {!this.state.loading && !this.state.pdfLoader && this.userId === 0 && (
                    <div className="info-box">
                        Please select a user to see the report.
                    </div>
                )}
                {!this.state.loading && !this.state.pdfLoader && this.userId !== 0 && (
                    <InteractionReportBlock history={this.props.history} match={this.props.match} location={this.props.location} notificationService={this.props.notificationService} FilterStore={this.props.FilterStore} />
                )}
            </PVAPage.Body>
            {!this.state.loading && <PVAPage.Footer />}
        </PVAPage>;
    }
}


// WEBPACK FOOTER //
// ./src/components/reports/interaction/InteractionReport.tsx